import * as React from 'react';
import * as R from 'ramda';
import { compose } from 'recompose';
import { withRouter, WithRouterProps } from 'react-router';
import Helmet from 'react-helmet';

import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';

import MediaQuery from 'ha/helpers/MediaQuery';
import { withIntl, IntlProps } from 'ha/i18n';
import units from 'ha/theme/units';
import { checkSearchQueryEmpty } from 'ha/utils/checkSearchQueryEmpty';
import Row from 'ha/ui/next/Row';

import { Page } from './types';
import PagerButton from './PagerButton';

interface Props {
  pager: {
    quantity: number;
    total: number;
  };
  pagerData: {
    firstPage: Page;
    lastPage: Page;
    prevPage: Page;
    nextPage: Page;
    visiblePages: Page[];
    headCollapsed: boolean;
    tailCollapsed: boolean;
  };
  onPageChange: (p: Page) => void;
  withHeadLinks?: boolean;
}

const styles = createStyles({
  container: {
    paddingTop: units.rem(0.5),
    paddingBottom: units.rem(0.5),
  },
});

export class Pager extends React.PureComponent<
  Props & WithRouterProps & IntlProps & WithStyles<typeof styles>
> {
  getPageHref = R.memoize((pageNum, { isHeadLink = false } = {}) => {
    const {
      router,
      location: { pathname, search, query, state },
    } = this.props;

    return router.createPath({
      pathname,
      search,
      state,
      query: {
        ...(!isHeadLink ? R.omit(['page'], query) : {}),
        ...(pageNum !== 1
          ? {
              page: pageNum,
            }
          : {}),
      },
    });
  });

  handlePageChange = (page: Page) => {
    this.props.onPageChange(page);
  };

  render() {
    const {
      classes,
      pager,
      pagerData: {
        firstPage,
        lastPage,
        prevPage,
        nextPage,
        visiblePages,
        headCollapsed,
        tailCollapsed,
      },
      withHeadLinks = true,
      location: { query },
      intl: { urlResolver },
    } = this.props;

    if (!pager.quantity || pager.total <= pager.quantity) {
      return null;
    }

    const isQueryEmpty = checkSearchQueryEmpty(query);

    return (
      <Row justify="flex-end" className={classes.container}>
        {prevPage && (
          <PagerButton
            page={prevPage}
            getPageHref={this.getPageHref}
            onPageChange={this.handlePageChange}
          >
            <ChevronLeft />
          </PagerButton>
        )}

        {prevPage && withHeadLinks && isQueryEmpty && (
          <Helmet>
            <link
              rel="prev"
              href={`${urlResolver.getBaseUrl()}${this.getPageHref(
                prevPage.num,
                { isHeadLink: true },
              )}`}
            />
          </Helmet>
        )}

        {headCollapsed && (
          <PagerButton
            page={firstPage}
            getPageHref={this.getPageHref}
            onPageChange={this.handlePageChange}
          />
        )}
        <MediaQuery>
          {({ lg }) =>
            lg && headCollapsed ? <Button disabled={true}>...</Button> : null
          }
        </MediaQuery>

        {visiblePages.map(page => (
          <PagerButton
            page={page}
            getPageHref={this.getPageHref}
            onPageChange={this.handlePageChange}
            key={page.num}
          />
        ))}

        <MediaQuery>
          {({ lg }) =>
            lg && tailCollapsed ? <Button disabled={true}>...</Button> : null
          }
        </MediaQuery>
        {tailCollapsed && (
          <PagerButton
            page={lastPage}
            getPageHref={this.getPageHref}
            onPageChange={this.handlePageChange}
            data-test-locator="Last Page Button"
          />
        )}

        {nextPage && (
          <PagerButton
            page={nextPage}
            getPageHref={this.getPageHref}
            onPageChange={this.handlePageChange}
            data-test-locator="Pager Next Page"
          >
            <ChevronRight />
          </PagerButton>
        )}

        {nextPage && withHeadLinks && isQueryEmpty && (
          <Helmet>
            <link
              rel="next"
              href={`${urlResolver.getBaseUrl()}${this.getPageHref(
                nextPage.num,
                { isHeadLink: true },
              )}`}
            />
          </Helmet>
        )}
      </Row>
    );
  }
}

const enhance = compose(
  withIntl,
  withRouter,
  withStyles(styles),
);

export default enhance(Pager);
